import React, { useEffect, useState, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  Menu,
  X,
  ChevronDown,
  Sparkles,
  LayoutDashboard,
  Star,
  Heart,
  PlusCircle,
  Tag,
  User,
  LogOut,
  Zap,
  Settings,
  Building2,
  Users,
  Home,
  BookOpen,
  Briefcase,
} from 'lucide-react';
import { theme, Button, Badge, Avatar } from '../../design-system';
import { getCookie } from '../../utils/csrf';
import { aiToolsData } from '../../data/aiToolsList';

// ============ Styled Components ============

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: ${theme.zIndices.sticky};
  background: ${theme.colors.neutral[900]};
  border-bottom: 1px solid ${theme.colors.neutral[800]};
`;

const NavContainer = styled.nav`
  max-width: ${theme.containerWidths['2xl']};
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;

  ${theme.media.lg} {
    padding: 0 ${theme.spacing[6]};
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  text-decoration: none;
  flex-shrink: 0;

  /* Push logo more to the left on larger screens for extra nav space */
  ${theme.media.lg} {
    margin-left: -${theme.spacing[6]};
  }

  ${theme.media.xl} {
    margin-left: -${theme.spacing[10]};
  }

  ${theme.media['2xl']} {
    margin-left: -${theme.spacing[12]};
  }

  img {
    height: 44px;
    width: auto;

    ${theme.media.sm} {
      height: 50px;
    }

    ${theme.media.md} {
      height: 48px;
    }

    ${theme.media.lg} {
      height: 56px;
    }
  }
`;

const DesktopNav = styled.div`
  display: none;
  align-items: center;
  gap: ${theme.spacing[1]};

  ${theme.media.lg} {
    display: flex;
  }
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.neutral[300]};
  text-decoration: none;
  border-radius: ${theme.radii.lg};
  transition: ${theme.transitions.button};
  white-space: nowrap;

  &:hover {
    color: ${theme.colors.neutral[0]};
    background: ${theme.colors.neutral[800]};
  }

  &.active {
    color: ${theme.colors.primary[400]};
    background: rgba(0, 147, 233, 0.1);
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.neutral[300]};
  background: transparent;
  border: none;
  border-radius: ${theme.radii.lg};
  cursor: pointer;
  transition: ${theme.transitions.button};

  &:hover {
    color: ${theme.colors.neutral[0]};
    background: ${theme.colors.neutral[800]};
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform ${theme.durations.fast} ${theme.easings.easeOut};
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    color: ${theme.colors.neutral[0]};
    background: ${theme.colors.neutral[800]};

    svg:last-child {
      transform: rotate(180deg);
    }
  `}
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + ${theme.spacing[2]});
  left: 0;
  min-width: 220px;
  max-height: 400px;
  overflow-y: auto;
  background: ${theme.colors.background.elevated};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.radii.xl};
  box-shadow: ${theme.shadows.xl};
  padding: ${theme.spacing[2]};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-10px')});
  transition: all ${theme.durations.fast} ${theme.easings.easeOut};
  z-index: ${theme.zIndices.dropdown};

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.neutral[300]};
    border-radius: ${theme.radii.full};
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  text-decoration: none;
  border-radius: ${theme.radii.md};
  transition: ${theme.transitions.button};

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.primary[500]};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${theme.colors.text.tertiary};
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: ${theme.colors.border.light};
  margin: ${theme.spacing[2]} 0;
`;

const DropdownLabel = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: ${theme.letterSpacings.wide};
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const CreditsDisplay = styled.div`
  display: none;
  align-items: center;
  gap: ${theme.spacing[1.5]};
  padding: ${theme.spacing[1.5]} ${theme.spacing[3]};
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid ${theme.colors.accent[500]};
  border-radius: ${theme.radii.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.accent[400]};

  ${theme.media.md} {
    display: flex;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(0, 147, 233, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(0, 147, 233, 0);
  }
`;

const SubmitButton = styled.a`
  display: none;

  ${theme.media.md} {
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing[2]};
    position: relative;
    padding: 10px 20px;
    font-size: ${theme.fontSizes.sm};
    font-weight: ${theme.fontWeights.bold};
    color: white;
    text-decoration: none;
    background: linear-gradient(135deg, #0093E9 0%, #80D0C7 50%, #0093E9 100%);
    background-size: 200% auto;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s ease;
    animation: ${shimmer} 3s linear infinite, ${pulse} 2s ease-in-out infinite;
    white-space: nowrap;
    flex-shrink: 0;
    box-shadow:
      0 4px 15px rgba(0, 147, 233, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      transition: left 0.5s ease;
    }

    &:hover {
      transform: translateY(-2px) scale(1.02);
      box-shadow:
        0 8px 25px rgba(0, 147, 233, 0.5),
        0 0 20px rgba(0, 147, 233, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
      animation: ${shimmer} 1.5s linear infinite;

      &::before {
        left: 100%;
      }
    }

    &:active {
      transform: translateY(0) scale(0.98);
    }

    svg {
      width: 16px;
      height: 16px;
      filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
    }
  }
`;

const SubmitButtonTrigger = styled.button`
  display: none;

  ${theme.media.md} {
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing[2]};
    position: relative;
    padding: 10px 20px;
    font-size: ${theme.fontSizes.sm};
    font-weight: ${theme.fontWeights.bold};
    color: white;
    background: linear-gradient(135deg, #0093E9 0%, #80D0C7 50%, #0093E9 100%);
    background-size: 200% auto;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s ease;
    animation: ${shimmer} 3s linear infinite, ${pulse} 2s ease-in-out infinite;
    white-space: nowrap;
    flex-shrink: 0;
    box-shadow:
      0 4px 15px rgba(0, 147, 233, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);

    &:hover {
      transform: translateY(-2px) scale(1.02);
      box-shadow:
        0 8px 25px rgba(0, 147, 233, 0.5),
        0 0 20px rgba(0, 147, 233, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }

    svg {
      width: 16px;
      height: 16px;
      transition: transform 0.2s ease;
    }

    svg:last-child {
      ${({ $isOpen }) => $isOpen && 'transform: rotate(180deg);'}
    }
  }
`;

const AddListingDropdown = styled.div`
  position: absolute;
  top: calc(100% + ${theme.spacing[2]});
  right: 0;
  min-width: 200px;
  background: ${theme.colors.background.elevated};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.radii.xl};
  box-shadow: ${theme.shadows.xl};
  padding: ${theme.spacing[2]};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-10px')});
  transition: all ${theme.durations.fast} ${theme.easings.easeOut};
  z-index: ${theme.zIndices.dropdown};
`;

const AddListingOption = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  text-decoration: none;
  border-radius: ${theme.radii.lg};
  transition: ${theme.transitions.button};

  &:hover {
    background: ${theme.colors.background.subtle};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${theme.colors.primary[500]};
  }

  span {
    display: flex;
    flex-direction: column;
  }

  .title {
    font-weight: ${theme.fontWeights.semibold};
  }

  .desc {
    font-size: ${theme.fontSizes.xs};
    color: ${theme.colors.text.tertiary};
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  color: ${theme.colors.neutral[300]};
  cursor: pointer;
  border-radius: ${theme.radii.lg};

  &:hover {
    color: ${theme.colors.neutral[0]};
    background: ${theme.colors.neutral[800]};
  }

  ${theme.media.lg} {
    display: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

// Mobile Menu
const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${theme.colors.background.overlay};
  z-index: ${theme.zIndices.overlay};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all ${theme.durations.normal} ${theme.easings.ease};

  ${theme.media.lg} {
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  max-width: 85vw;
  background: ${theme.colors.background.primary};
  z-index: ${theme.zIndices.modal};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform ${theme.durations.normal} ${theme.easings.easeOut};
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  ${theme.media.lg} {
    display: none;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.neutral[900]};
  border-bottom: 1px solid ${theme.colors.neutral[800]};
`;

const MobileCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  color: ${theme.colors.neutral[300]};
  cursor: pointer;
  border-radius: ${theme.radii.lg};

  &:hover {
    background: ${theme.colors.neutral[800]};
    color: ${theme.colors.neutral[0]};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileNav = styled.nav`
  flex: 1;
  padding: ${theme.spacing[4]};
`;

const MobileNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
  text-decoration: none;
  border-radius: ${theme.radii.lg};

  &:hover,
  &.active {
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[600]};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${theme.colors.text.tertiary};
  }
`;

const MobileNavButton = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
  text-decoration: none;
  border-radius: ${theme.radii.lg};

  &:hover {
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[600]};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${theme.colors.text.tertiary};
  }
`;

const MobileSection = styled.div`
  padding-top: ${theme.spacing[4]};
  margin-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.light};
`;

const MobileSectionTitle = styled.div`
  padding: 0 ${theme.spacing[4]} ${theme.spacing[2]};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: ${theme.letterSpacings.wide};
`;

const MobileFooter = styled.div`
  padding: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.light};
`;

const MobileCredits = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid ${theme.colors.accent[500]};
  border-radius: ${theme.radii.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.accent[600]};
  margin-bottom: ${theme.spacing[3]};

  svg {
    width: 18px;
    height: 18px;
  }
`;

// ============ Component ============

const NavbarNew = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credits, setCredits] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const aiToolsRef = useRef(null);
  const realEstateRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Check auth status
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch('/api/credits/auth-status-with-credits/', {
          credentials: 'include',
        });

        if (!response.ok) {
          const fallbackResponse = await fetch('/api/auth-status/', {
            credentials: 'include',
          });
          const fallbackData = await fallbackResponse.json();
          setIsAuthenticated(fallbackData.isAuthenticated);
          return;
        }

        const data = await response.json();
        setIsAuthenticated(data.isAuthenticated);
        if (data.isAuthenticated) {
          setCredits(data.credits);
        }
      } catch (error) {
        try {
          const fallbackResponse = await fetch('/api/auth-status/', {
            credentials: 'include',
          });
          const fallbackData = await fallbackResponse.json();
          setIsAuthenticated(fallbackData.isAuthenticated);
        } catch (e) {
          console.error('Error checking auth status:', e);
        }
      }
    };

    checkAuthStatus();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        aiToolsRef.current &&
        !aiToolsRef.current.contains(e.target) &&
        realEstateRef.current &&
        !realEstateRef.current.contains(e.target) &&
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/logout/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      if (response.ok) {
        setIsAuthenticated(false);
        window.location.href = '/?logout_success=true';
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const getLoginUrl = () => {
    const path = window.location.pathname + window.location.search;
    return `/api/login/?next=${encodeURIComponent(path)}`;
  };

  const availableAiTools = aiToolsData.filter((tool) => !tool.comingSoon);

  const navItems = [
    { to: '/', label: 'Home', exact: true },
    { to: '/product-search', label: 'Products' },
    // Real Estate is now a dropdown
    { to: '/all-listings', label: 'Directory' },
    { to: '/deals-list', label: 'Deals' },
    { to: '/jobs-list', label: 'Jobs' },
    { to: '/classifieds', label: 'Classifieds' },
    { to: '/blog', label: 'Blog' },
    { to: '/how-it-works', label: 'How It Works' },
  ];

  return (
    <Header>
      <NavContainer>
        {/* Logo */}
        <LogoLink to="/">
          <img
            src={`${process.env.PUBLIC_URL}/static/images/df_logo.svg`}
            alt="Desi Firms"
            width="160"
            height="56"
          />
        </LogoLink>

        {/* Desktop Navigation */}
        <DesktopNav>
          {navItems.slice(0, 1).map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              end={item.exact}
            >
              {item.label}
            </NavItem>
          ))}

          {/* AI Tools Dropdown */}
          <DropdownWrapper
            ref={aiToolsRef}
            onMouseEnter={() => setOpenDropdown('aitools')}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <DropdownTrigger $isOpen={openDropdown === 'aitools'}>
              <Sparkles size={16} />
              AI Tools
              <ChevronDown />
            </DropdownTrigger>
            <DropdownMenu $isOpen={openDropdown === 'aitools'}>
              <DropdownItem to="/ai-tools">
                <span>All AI Tools</span>
              </DropdownItem>
              <DropdownItem to="/learning-hub">
                <span>Learning Hub</span>
                <BookOpen size={16} />
              </DropdownItem>
              <DropdownDivider />
              {availableAiTools.slice(0, 8).map((tool) => (
                <DropdownItem key={tool.id} to={tool.path}>
                  <span>{tool.title}</span>
                  {tool.requiresCredits && (
                    <Badge variant="primary" size="xs">
                      <Zap size={10} /> {tool.creditCost}
                    </Badge>
                  )}
                </DropdownItem>
              ))}
              {availableAiTools.length > 8 && (
                <>
                  <DropdownDivider />
                  <DropdownItem to="/ai-tools">
                    <span>View all {availableAiTools.length} tools →</span>
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </DropdownWrapper>

          {/* Real Estate Dropdown */}
          <DropdownWrapper
            ref={realEstateRef}
            onMouseEnter={() => setOpenDropdown('realestate')}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <DropdownTrigger $isOpen={openDropdown === 'realestate'}>
              Real Estate
              <ChevronDown />
            </DropdownTrigger>
            <DropdownMenu $isOpen={openDropdown === 'realestate'}>
              <DropdownItem to="/realestate">
                <span>Browse Properties</span>
                <Home size={16} />
              </DropdownItem>
              <DropdownDivider />
              <DropdownItem to="/realestate/find-agents">
                <span>Find Agents</span>
                <Users size={16} />
              </DropdownItem>
              <DropdownItem to="/realestate/find-agencies">
                <span>Find Agencies</span>
                <Building2 size={16} />
              </DropdownItem>
            </DropdownMenu>
          </DropdownWrapper>

          {navItems.slice(1).map((item) => (
            <NavItem key={item.to} to={item.to}>
              {item.label}
            </NavItem>
          ))}

          {/* User Menu Dropdown */}
          <DropdownWrapper
            ref={userMenuRef}
            onMouseEnter={() => setOpenDropdown('user')}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <DropdownTrigger $isOpen={openDropdown === 'user'}>
              <User size={16} />
              {isAuthenticated ? 'Account' : 'Sign In'}
              <ChevronDown />
            </DropdownTrigger>
            <DropdownMenu $isOpen={openDropdown === 'user'}>
              {isAuthenticated ? (
                <>
                  <DropdownLabel>Dashboard</DropdownLabel>
                  <DropdownItem as="a" href="/dashboard/">
                    <span>Overview</span>
                    <LayoutDashboard />
                  </DropdownItem>
                  <DropdownItem as="a" href="/dashboard/reviews/">
                    <span>Reviews</span>
                    <Star />
                  </DropdownItem>
                  <DropdownItem as="a" href="/dashboard/interactions/">
                    <span>Interactions</span>
                    <Heart />
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownLabel>Create</DropdownLabel>
                  <DropdownItem as="a" href="/api/add-listing/">
                    <span>Add Business</span>
                    <Building2 />
                  </DropdownItem>
                  <DropdownItem as="a" href="/realestate/dashboard/">
                    <span>Real Estate</span>
                    <Home />
                  </DropdownItem>
                  <DropdownItem as="a" href="/api/add_deal/">
                    <span>Add Deal</span>
                    <Tag />
                  </DropdownItem>
                  <DropdownItem as="a" href="/api/add_job/">
                    <span>Post a Job</span>
                    <Briefcase />
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem as="a" href="/api/credits/ai-credits-manage/">
                    <span>AI Credits</span>
                    <Badge variant="warning" size="xs">
                      <Zap size={10} /> {credits}
                    </Badge>
                  </DropdownItem>
                  <DropdownItem as="a" href="/api/profile/">
                    <span>My Profile</span>
                    <Settings />
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem as="a" href="#" onClick={handleLogout}>
                    <span>Logout</span>
                    <LogOut />
                  </DropdownItem>
                </>
              ) : (
                <>
                  <DropdownItem as="a" href={getLoginUrl()}>
                    <span>Sign In</span>
                  </DropdownItem>
                  <DropdownItem
                    as="a"
                    href={`/api/register/?next=${encodeURIComponent(
                      window.location.pathname
                    )}`}
                  >
                    <span>Create Account</span>
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </DropdownWrapper>
        </DesktopNav>

        {/* Right Section */}
        <RightSection>
          <DropdownWrapper
            onMouseEnter={() => setOpenDropdown('addlisting')}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <SubmitButtonTrigger $isOpen={openDropdown === 'addlisting'}>
              <PlusCircle size={16} />
              Add Listing
              <ChevronDown size={14} />
            </SubmitButtonTrigger>
            <AddListingDropdown $isOpen={openDropdown === 'addlisting'}>
              <AddListingOption href="/api/add-listing/">
                <Building2 />
                <span>
                  <span className="title">Add Business</span>
                  <span className="desc">List your business on directory</span>
                </span>
              </AddListingOption>
              <AddListingOption href="/realestate/dashboard/">
                <Home />
                <span>
                  <span className="title">Real Estate Agent</span>
                  <span className="desc">Register as agent or create agency</span>
                </span>
              </AddListingOption>
            </AddListingDropdown>
          </DropdownWrapper>

          <MobileMenuButton
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </MobileMenuButton>
        </RightSection>
      </NavContainer>

      {/* Mobile Menu */}
      <MobileOverlay
        $isOpen={isMobileMenuOpen}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <MobileMenu $isOpen={isMobileMenuOpen}>
        <MobileHeader>
          <LogoLink to="/" onClick={() => setIsMobileMenuOpen(false)}>
            <img
              src={`${process.env.PUBLIC_URL}/static/images/df_logo.svg`}
              alt="Desi Firms"
              width="90"
              height="32"
              style={{ height: 32 }}
            />
          </LogoLink>
          <MobileCloseButton
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X />
          </MobileCloseButton>
        </MobileHeader>

        <MobileNav>
          {navItems.map((item) => (
            <MobileNavItem
              key={item.to}
              to={item.to}
              end={item.exact}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item.label}
            </MobileNavItem>
          ))}

          <MobileNavItem to="/ai-tools" onClick={() => setIsMobileMenuOpen(false)}>
            <Sparkles />
            AI Tools
          </MobileNavItem>
          <MobileNavItem to="/learning-hub" onClick={() => setIsMobileMenuOpen(false)}>
            <BookOpen />
            Learning Hub
          </MobileNavItem>

          <MobileSection>
            <MobileSectionTitle>Real Estate</MobileSectionTitle>
            <MobileNavItem to="/realestate" onClick={() => setIsMobileMenuOpen(false)}>
              <Home />
              Browse Properties
            </MobileNavItem>
            <MobileNavItem to="/realestate/find-agents" onClick={() => setIsMobileMenuOpen(false)}>
              <Users />
              Find Agents
            </MobileNavItem>
            <MobileNavItem to="/realestate/find-agencies" onClick={() => setIsMobileMenuOpen(false)}>
              <Building2 />
              Find Agencies
            </MobileNavItem>
          </MobileSection>

          {isAuthenticated && (
            <MobileSection>
              <MobileSectionTitle>Dashboard</MobileSectionTitle>
              <MobileNavButton href="/dashboard/">
                <LayoutDashboard />
                Overview
              </MobileNavButton>
              <MobileSectionTitle style={{ marginTop: 12 }}>Add Listing</MobileSectionTitle>
              <MobileNavButton href="/api/add-listing/">
                <Building2 />
                Add Business
              </MobileNavButton>
              <MobileNavButton href="/realestate/dashboard/">
                <Home />
                Real Estate
              </MobileNavButton>
              <MobileNavButton href="/api/credits/ai-credits-manage/">
                <Zap />
                AI Credits
              </MobileNavButton>
              <MobileNavButton href="/api/profile/">
                <User />
                My Profile
              </MobileNavButton>
            </MobileSection>
          )}
        </MobileNav>

        <MobileFooter>
          {isAuthenticated ? (
            <Button
              variant="outline"
              fullWidth
              onClick={handleLogout}
              leftIcon={<LogOut size={18} />}
            >
              Logout
            </Button>
          ) : (
            <Button
              as="a"
              href={getLoginUrl()}
              variant="primary"
              fullWidth
            >
              Sign In
            </Button>
          )}
        </MobileFooter>
      </MobileMenu>
    </Header>
  );
};

export default NavbarNew;
